import { useEffect, useRef, useState } from "react";
import { menuItems, type MenuItem } from "../lib/assignmentActions";
import { useTopLayerMenu } from "./useTopLayerMenu";

/* W11b — THE DAY CARD'S `⋯` MENU (§8g).
 *
 * ⚠️ IT CARRIES ONLY ACTIONS THE LIST UNDER THE GRID ALREADY PERFORMS, and both surfaces call the
 * same `assignmentActions` module — the items themselves come from `menuItems`, so the two cannot
 * offer different ones. No Log (a coach does not log for the athlete) and no Copy (there is no
 * paste anywhere in this app, `EVERFIT-MAP` §27.5, and a copy with no paste teaches a coach the
 * product is broken).
 *
 * 🔴 KEYBOARD-REACHABLE, AND THEIRS IS NOT — that is the falsifiable half of "better than
 * Everfit" on this screen, and it is cheap: a real `<button>`, Escape to close, focus returned to
 * the trigger, and arrow keys through the items. A menu that only a mouse can open hides three
 * destructive actions from anyone who does not use one.
 *
 * ⚠️ AND IT IS A MENU, NOT A DIALOG. `Remove` does not confirm HERE — it hands back to the caller,
 * which owns `removalConsequence`'s sentence. A confirm written in a popover would be a second
 * copy of the one rule that must never understate what a removal destroys.
 */
export function AssignmentMenu({ assignment, label, onChoose, items: givenItems }: {
  assignment: { id: string; hiddenFromClient: boolean; trained?: boolean };
  /** A surface with no day window names the items it can honour (the Tasks tab: Remove… alone);
   *  absent = `menuItems`, the one list computed from the day's state. */
  items?: MenuItem[];
  /** Named in the trigger's accessible label, so a screen reader hears WHICH day's menu. */
  label: string;
  onChoose: (item: MenuItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const items = givenItems ?? menuItems(assignment);

  /* W79 — the list is drawn in the top layer, placed under its `⋯`: `useTopLayerMenu` (one mechanism, both menus). */
  useTopLayerMenu(open, trigger, list);

  useEffect(() => {
    if (!open) return;
    // Focus lands in the menu on open — otherwise a keyboard user opens a menu they are not in.
    const first = list.current?.querySelector<HTMLButtonElement>("button");
    first?.focus();
  }, [open]);

  function close(restoreFocus: boolean) {
    setOpen(false);
    setActive(0);
    // ⚠️ FOCUS GOES BACK TO THE TRIGGER, or a keyboard user is returned to the top of the page.
    if (restoreFocus) trigger.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.stopPropagation(); close(true); return; }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = e.key === "ArrowDown"
        ? (active + 1) % items.length
        : (active - 1 + items.length) % items.length;
      setActive(next);
      list.current?.querySelectorAll<HTMLButtonElement>("button")[next]?.focus();
    }
  }

  /* 🔴 NO ITEMS, NO TRIGGER — W22(a). `menuItems` empties on a trained day (their own rule, read
     live), and a `⋯` that opens an empty popover is worse than none: it tells a coach there is
     something here and then refuses to say what. The day's actions live on the Training tab's
     list, which is where `removalConsequence` can print what a removal would destroy. */
  if (items.length === 0) return null;

  return (
    <div className="assignment-menu" onKeyDown={onKeyDown}>
      <button ref={trigger} type="button" className="assignment-menu-trigger"
              aria-haspopup="menu" aria-expanded={open}
              aria-label={`Actions for ${label}`}
              onClick={() => setOpen(!open)}>⋯</button>
      {open && (
        <ul ref={list} className="assignment-menu-list is-top-layer" role="menu"
            aria-label={`Actions for ${label}`}>
          {items.map((item) => (
            <li key={item.kind} role="none">
              <button type="button" role="menuitem"
                      className={item.kind === "remove"
                        ? "assignment-menu-item is-destructive" : "assignment-menu-item"}
                      onClick={() => { close(false); onChoose(item); }}>
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* ⚠️ A CLICK OUTSIDE CLOSES IT WITHOUT RESTORING FOCUS — the coach has already moved their
          attention elsewhere, and yanking the caret back to a `⋯` they have left is the kind of
          focus steal this repo has fixed before. */}
      {open && <div className="assignment-menu-scrim" onClick={() => close(false)} aria-hidden="true" />}
    </div>
  );
}

/* W108's `AssignmentActions` (the ⋯'s items as words at the card's foot, on hover) is GONE since the hover row's brief (10-16):
 * hovered, the words covered the card's date and name (MEASURED, 1024 + 1440). The words are this menu's again, on both
 * pointer kinds (`cardMenuItems`). */
